import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Form } from "reactstrap";
import { useTranslation } from "react-i18next";
import { handleEditFormSubmit } from "../../../components/modals/EditableTable";
import { toast } from "react-toastify";
import { useHistory, useLocation } from "react-router-dom";
import { useIsAdminViewMode } from "../../../hooks/useViewMode";

import ZoneSettingsContenantGrid from "./ZoneSettingsContenantGrid";

const ZoneSettingsContenant = ({
  container,
  token,
  onFormChange,
  onContainerSaved,
  onContainerDraftChange,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const API = process.env.REACT_APP_API_BACKEND;
  const initialSnapshotRef = useRef(null);

  const [formData, setFormData] = useState(() => ({ ...container }));
  const [sendingForm, setSendingForm] = useState(false);
  const [errorForm, setErrorForm] = useState("");
  const [sites, setSites] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [wasteCategories, setWasteCategories] = useState([]);
  const syncedContainerIdRef = useRef(null);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  const adminUserConnected = useIsAdminViewMode();

  /* Sync form from server when the zone identity changes, or while the form is still pristine.
     Never overwrite a dirty draft just because the parent received a new object reference. */
  useEffect(() => {
    if (!container || Object.keys(container).length === 0) return;

    const next = {
      ...container,
      displayColor: container.displayColor || "#fff",
    };
    const containerId = String(container._id || container._measurement || "");
    const idChanged = syncedContainerIdRef.current !== containerId;
    const currentForm = formDataRef.current;
    const notDirty =
      initialSnapshotRef.current == null ||
      JSON.stringify(currentForm) === initialSnapshotRef.current;

    if (!idChanged && !notDirty) return;

    const nextJson = JSON.stringify(next);
    if (!idChanged && nextJson === JSON.stringify(currentForm)) {
      syncedContainerIdRef.current = containerId;
      return;
    }

    syncedContainerIdRef.current = containerId;
    setFormData(next);
    initialSnapshotRef.current = nextJson;
  }, [container]);

  /* Signaler si le formulaire a été modifié par rapport à l’état initial. */
  useEffect(() => {
    if (
      typeof onFormChange !== "function" ||
      initialSnapshotRef.current == null
    )
      return;
    const hasChanges = JSON.stringify(formData) !== initialSnapshotRef.current;
    onFormChange(hasChanges);
  }, [formData, onFormChange]);

  useEffect(() => {
    if (typeof onContainerDraftChange !== "function") return;
    onContainerDraftChange(formData);
  }, [formData, onContainerDraftChange]);

  useEffect(() => {
    if (!token) return;
    const fetchWasteCategories = async () => {
      try {
        const response = await axios.get(
          `${API}wastecategory?page=1&limit=500`,
          {
            headers: { authorization: `Bearer ${token}` },
            params: {
              keywordFilter: "",
              listUnCodeFullFilter: {},
              listUnCodeEmptyFilter: {},
              listWasteCodeFullFilter: {},
              listWasteCodeEmptyFilter: {},
              sortByField: "label",
              orderSortByField: "1",
            },
          },
        );
        if (response?.status === 200) {
          setWasteCategories(response.data?.data || []);
        }
      } catch {
        setWasteCategories([]);
      }
    };
    fetchWasteCategories();
  }, [API, token]);

  /* Quand le flux change, aligner authorizedMaterial pour l’onglet Matériaux non autorisés. */
  useEffect(() => {
    if (!wasteCategories.length) return;

    const flowId =
      formData.contentCategory == null || formData.contentCategory === ""
        ? ""
        : String(formData.contentCategory);

    if (!flowId) {
      const hasAuthorized =
        (formData.contentCategoryJoin?.authorizedMaterial || []).length > 0;
      if (!hasAuthorized) return;
      setFormData((prev) => ({
        ...prev,
        contentCategoryJoin: {
          ...(prev.contentCategoryJoin || {}),
          _id: "",
          name: "",
          label: "",
          authorizedMaterial: [],
        },
      }));
      return;
    }

    const selected = wasteCategories.find((wc) => String(wc._id) === flowId);
    if (!selected) return;

    const joinId = formData.contentCategoryJoin?._id
      ? String(formData.contentCategoryJoin._id)
      : "";
    const nextAuthorized = selected.authorizedMaterial || [];
    const currentAuthorized =
      formData.contentCategoryJoin?.authorizedMaterial || [];
    const sameJoin =
      joinId === flowId &&
      JSON.stringify(currentAuthorized) === JSON.stringify(nextAuthorized);

    if (sameJoin) return;

    setFormData((prev) => ({
      ...prev,
      contentCategoryJoin: {
        ...(prev.contentCategoryJoin || {}),
        _id: selected._id,
        name: selected.label,
        label: selected.label,
        authorizedMaterial: nextAuthorized,
      },
    }));
  }, [formData.contentCategory, formData.contentCategoryJoin, wasteCategories]);

  useEffect(() => {
    if (container?.displayColor) return;
    setFormData((prev) =>
      prev?.displayColor === "#fff" ? prev : { ...prev, displayColor: "#fff" },
    );
  }, [container?._id, container?.displayColor]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [responseSite, responseContract] = await axios.all([
          axios.get(`${API}address/getAddressNameList`, {
            headers: { authorization: `Bearer ${token}` },
          }),
          axios.get(`${API}contract/getContractList`, {
            headers: { authorization: `Bearer ${token}` },
          }),
        ]);
        if (responseSite?.status === 200)
          setSites(responseSite.data?.addressNameList || []);
        if (responseContract?.status === 200)
          setContracts(responseContract.data?.contractsList || []);
      } finally {
      }
    };
    fetchData();
  }, [API, token]);

  /* Scroll vers la section Gestion quand on arrive depuis "Modifier le flux" (scrollTo=gestion). */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("scrollTo") !== "gestion") return;
    const el = document.getElementById("zone-settings-gestion");
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      history.replace({
        pathname: location.pathname,
        search: "?tab=contenant",
      });
    });
  }, [location.search, location.pathname, history]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (errorForm || sendingForm || formData?.fictiveContainer?.active) return;
    setSendingForm(true);
    try {
      const response = await handleEditFormSubmit(formData, "container");
      if (response?.status === 200) {
        toast.success(t("containerSettings.containerUpdated"), {
          className: "custom-toast",
        });
        const saved = response?.data?.data || formData;
        const next = {
          ...formData,
          ...saved,
          displayColor: saved.displayColor || formData.displayColor || "#fff",
        };
        setFormData(next);
        initialSnapshotRef.current = JSON.stringify(next);
        if (typeof onContainerSaved === "function") {
          onContainerSaved(saved);
        }
        setSendingForm(false);
      } else {
        setSendingForm(false);
        if (response?.data?.error) console.error(response.data.error);
      }
    } catch (err) {
      setSendingForm(false);
    }
  };

  return (
    <Form
      id="zone-settings-contenant-form"
      onSubmit={handleSubmit}
      className="zone-settings__contenant-form flex flex-col"
    >
      {errorForm && (
        <div className="mb-4 p-3 bg-white rounded shadow-sm border border-red-200 text-red-700">
          <span dangerouslySetInnerHTML={{ __html: errorForm }} />
        </div>
      )}

      <ZoneSettingsContenantGrid
        formData={formData}
        setFormData={setFormData}
        setErrorForm={setErrorForm}
        sites={sites}
        contracts={contracts}
        showAdministrator={adminUserConnected}
      />
    </Form>
  );
};

export default ZoneSettingsContenant;
